import { useState, useRef, useCallback } from 'react';

export type RecordingState = 'idle' | 'recording' | 'processing';

interface UseAudioRecorderReturn {
  recordingState: RecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  cancelRecording: () => void;
  hasPermission: boolean;
}

const PERMISSION_STORAGE_KEY = 'audio-recording-permission-granted';

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [hasPermission, setHasPermission] = useState(() => {
    return localStorage.getItem(PERMISSION_STORAGE_KEY) === 'true';
  });
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Store permission in localStorage
      localStorage.setItem(PERMISSION_STORAGE_KEY, 'true');
      setHasPermission(true);
      
      return true;
    } catch (error) {
      console.error('Failed to get microphone permission:', error);
      localStorage.setItem(PERMISSION_STORAGE_KEY, 'false');
      setHasPermission(false);
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    // Request permission if not already granted
    if (!hasPermission || !streamRef.current) {
      const granted = await requestPermission();
      if (!granted) {
        throw new Error('Microphone permission denied');
      }
    }

    // Reset chunks
    audioChunksRef.current = [];

    try {
      const stream = streamRef.current!;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setRecordingState('recording');
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw error;
    }
  }, [hasPermission, requestPermission]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Release media stream tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        setRecordingState('idle');
        resolve(audioBlob);
      };

      mediaRecorder.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    
    // Release media stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    audioChunksRef.current = [];
    setRecordingState('idle');
  }, []);

  return {
    recordingState,
    startRecording,
    stopRecording,
    cancelRecording,
    hasPermission,
  };
}
